import EmberRouter from '@embroider/router';
import { getOwner } from '@ember/application';
import { action } from '@ember/object';
import moment from 'moment-timezone';
import { inject as service } from '@ember/service';
import generateTransitionEvent from 'cio/utils/analytics/generate-transition-event';
import { scheduleOnce } from '@ember/runloop';

import config from './config/environment';

export default class AppRouter extends EmberRouter {
  @service auth;
  @service account;
  @service routeHistory;
  @service router;
  @service hydra;

  location = config.locationType;
  rootURL = config.rootURL;
  previousRouteEnterTimestamp = null;
  previousRouteName = null;
  previousRoute = null;

  constructor() {
    super(...arguments);

    this.router.on('routeDidChange', () => {
      this.onTransition();
    });

    if (this.hydra.isHydraEnabled && this.hydra.config.rootUrl != null) {
      this.rootURL = this.hydra.config.rootUrl;
    }
  }

  onTransition() {
    this.notifyAnalytics();
  }

  @action
  notifyAnalytics() {
    let owner = getOwner(this);
    let analytics = owner.lookup('service:analytics');
    let application = owner.lookup('controller:application');
    let { previousRoute, previousRouteName } = this.routeHistory;

    if (application.masq.isOn) {
      return;
    }

    let { currentRoute, currentRouteName } = this.router;
    // In Ember 4, sometimes notifyAnalytics seems to be called twice
    // once when this.router.currentRoute is set, once when not
    //
    // let's detect this scenario and return early to avoid errors:
    if (currentRouteName != null && currentRoute == null) {
      return;
    }

    let event = generateTransitionEvent(currentRoute, {
      previousRouteName,
      previousRoute,
    });

    if (event) {
      let extraData = {};
      // Find instance of current route, if it exists
      let currentRouteInstance = owner.lookup(`route:${currentRouteName}`);

      // Check if the route specifies additional analytics data to be included
      // in the transition event.
      // If yes, add it to "passThroughProperties", which will be included in
      // the analytics payload as-is
      if (currentRouteInstance?.analyticsTransitionEventData != null) {
        extraData = currentRouteInstance.analyticsTransitionEventData();
      }

      // Use `afterRender` to allow components to render completely before
      // sending an event. This ensures that we're able to correctly find
      // information about an element's visibility if an event needs it.
      scheduleOnce('afterRender', this, () => {
        analytics.trackTransition(event, {
          model: currentRoute.attributes,
          route: currentRoute,
          previousRouteName,
          previousRoute,
          passThroughProperties: extraData,
        });
      });
    }

    this.routeHistory.setPreviousRoute(moment(), currentRouteName, this.router.currentRoute);
  }
}

function workspaceRoutes(dslCallback) {
  dslCallback.route('errors', { path: 'errors/:error_id' });

  dslCallback.route('dashboard', {
    resetNamespace: true,
  });

  dslCallback.route('setup-guide', { resetNamespace: true }, function () {
    this.route('welcome');
    this.route('sources');
    this.route('channels', function () {
      this.route('slack');
      this.route('twilio');
      this.route('email');
      this.route('push');
      this.route('webhook');
      this.route('in_app');
    });
    this.route('create-campaign');
    this.route('send-campaign');
  });

  dslCallback.route('composer', { resetNamespace: true }, function () {
    this.route('action', { path: 'actions/:action_id' });
    this.route(
      'transactional',
      {
        path: 'transactional/:transactional_message_id',
      },
      function () {
        this.route('template', {
          path: 'templates/:template_id',
        });
      },
    );

    this.route('newsletter', { path: 'newsletters/:newsletter_id' }, function () {
      this.route('template', {
        path: 'templates/:template_id',
      });
    });
  });

  dslCallback.route('segments', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('new');
    this.route('segment', { path: ':segment_id' }, function () {
      this.route('edit');

      this.route('overview', function () {
        this.route('index', { path: '/' });
        this.route('edit');
      });

      this.route('people');

      this.route('usage');
      this.route('audiences');

      this.route('imports', function () {
        this.route('csv', function () {
          this.route('new');

          this.route('setup', { path: '/setup/:import_id' }, function () {
            this.route('map');
            this.route('review');
            this.route('preprocessing');
            this.route('validating');
            this.route('importing');
          });
        });
      });
    });
  });

  dslCallback.route('transactional', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('new');
    this.route('join-beta');

    this.route('message', { path: ':transactional_message_id' }, function () {
      this.route('setup', function () {
        this.route('settings');
        this.route('review');
      });

      this.route('overview', function () {
        this.route('metrics');
        this.route('sent', function () {
          this.route('delivery', { path: '/:delivery_id' });
        });

        this.route('drafts');
      });
    });
  });

  dslCallback.route('campaigns', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('new');
    this.route('welcome', function () {
      this.route('index', { path: '/' });
      this.route('create');
    });

    this.route('campaign', { path: ':campaign_id' }, function () {
      this.route('index', { path: '/' });
      this.route('unfollow');
      this.route('overview', function () {
        this.route('index', { path: '/' });
        this.route('journey_metrics');
        this.route('metrics');
        this.route('drafts', function () {
          this.route('index', { path: '/' });
          this.route('draft', { path: ':delivery_id' });
        });
        this.route('sent', function () {
          this.route('index', { path: '/' });
          this.route('delivery', { path: ':delivery_id' });
        });
        this.route('tests', function () {
          this.route('index', { path: '/' });
          this.route('test', { path: '/:action_id' });
        });
        this.route('trigger', function () {
          this.route('index', { path: '/' });
        });
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
        this.route('workflow', function () {
          this.route('actions');
        });
        this.route('goal', function () {
          this.route('index', { path: '/' });
        });
        this.route('subjects', { path: '/journeys' }, function () {
          this.route('index', { path: '/' });
          this.route('subject', { path: '/:subject_id' });
        });
        this.route('settings', function () {
          this.route('index', { path: '/' });
        });
      });
      this.route('setup', function () {
        this.route('trigger', function () {
          this.route('index', { path: '/' });
        });
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
        this.route('settings', function () {
          this.route('index', { path: '/' });
        });
        this.route('goal', function () {
          this.route('index', { path: '/' });
        });
        this.route('workflow', function () {
          this.route('actions');
        });
        this.route('review', function () {
          this.route('index', { path: '/' });
        });
      });
      this.route('workflow');
    });
  });

  dslCallback.route('broadcasts', { resetNamespace: true }, function () {
    this.route('new');

    this.route('list', { path: '/' }, function () {
      this.route('newsletters', function () {
        this.route('index', { path: '/' });
      });
      this.route('api-triggered', function () {
        this.route('index', { path: '/' });
      });
    });

    this.route('broadcast', { path: 'broadcast/:campaign_id' }, function () {
      this.route('overview', function () {
        this.route('index', { path: '/' });
        this.route('metrics');
        this.route('journey_metrics');
        this.route('drafts', function () {
          this.route('index', { path: '/' });
          this.route('draft', { path: ':delivery_id' });
        });
        this.route('triggering_details', function () {
          this.route('index', { path: '/' });
        });
        this.route('broadcasts', function () {
          this.route('index', { path: '/' });
          this.route('trigger', { path: ':trigger_id' }, function () {
            this.route('index', { path: '/' });
            this.route('delivery', { path: ':delivery_id' });
          });
        });
        this.route('tests', function () {
          this.route('index', { path: '/' });
          this.route('test', { path: '/:action_id' });
        });
        this.route('trigger', function () {
          this.route('index', { path: '/' });
        });
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
        this.route('settings', function () {
          this.route('index', { path: '/' });
        });
        this.route('workflow', function () {
          this.route('actions');
        });
        this.route('goal', function () {
          this.route('index', { path: '/' });
        });
        this.route('subjects', { path: 'journeys' }, function () {
          this.route('index', { path: '/' });
        });
      });
      this.route('setup', function () {
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
        this.route('goal', function () {
          this.route('index', { path: '/' });
        });
        this.route('workflow', function () {
          this.route('actions');
        });
        this.route('review', function () {
          this.route('index', { path: '/' });
        });
        this.route('trigger', function () {
          this.route('index', { path: '/' });
        });
        this.route('settings', function () {
          this.route('index', { path: '/' });
        });
      });
      this.route('workflow');
    });

    this.route('newsletter', { path: 'newsletter/:newsletter_id' }, function () {
      this.route('overview', function () {
        this.route('reports', function () {
          this.route('index', { path: '/' });
        });
        this.route('test', function () {
          this.route('index', { path: '/' });
        });
        this.route('content', function () {
          this.route('index', { path: '/' });
        });
        this.route('sent', function () {
          this.route('index', { path: '/' });
          this.route('delivery', { path: ':delivery_id' });
        });
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
      });
      this.route('setup', function () {
        this.route('recipients', function () {
          this.route('index', { path: '/' });
        });
        this.route('content', function () {
          this.route('index', { path: '/' });
        });
        this.route('tracking', function () {
          this.route('index', { path: '/' });
        });
        this.route('review', function () {
          this.route('index', { path: '/' });
        });
      });
    });
  });

  dslCallback.route('layouts', { resetNamespace: true }, function () {
    this.route('new');
    this.route('starters');
    this.route('layout', { path: ':layout_id', resetNamespace: true }, function () {
      this.route('index', { path: '/' });
      this.route('edit');
    });
  });

  dslCallback.route('people', { resetNamespace: true }, function () {
    this.route('new');
    this.route('merge');
    this.route('list', { path: '/' }, function () {
      this.route('index', { path: '/' });
    });
    this.route('person', { path: ':person_id', resetNamespace: true }, function () {
      this.route('person.segments', { path: '/segments', resetNamespace: true }, function () {
        this.route('belongs');
        this.route('others');
      });
      this.route('devices');
      this.route('drafts');
      this.route('sent');
      this.route('person.attributes', { path: '/attributes', resetNamespace: true }, function () {
        this.route('edit');
      });
      this.route('relationships');

      this.route('activity');

      this.route('subjects', { path: '/journeys' }, function () {
        this.route('index', { path: '/' });
        this.route('subject', { path: '/:subject_id' });
      });
    });
  });

  dslCallback.route('objects', { resetNamespace: true }, function () {
    this.route('type', { path: ':slug' }, function () {
      this.route('list', { path: '/' });
      this.route('guide');
      this.route('new');
      this.route('object', { path: ':object_id', resetNamespace: true }, function () {
        this.route('attributes', function () {
          this.route('edit');
        });
        this.route('relationships');
        this.route('activity');
      });
    });
  });

  // We used to have drafts as a top-level route, not nested within
  // outbox.  We're keeping this here so we can redirect to
  // `outbox.list.drafts` from the route file.
  dslCallback.route('drafts', { resetNamespace: true });
  dslCallback.route('deliveries', { resetNamespace: true });

  // Delivery Logs (Outbox)
  dslCallback.route('outbox', { resetNamespace: true }, function () {
    this.route('list', { path: '/' }, function () {
      this.route('deliveries');
      this.route('drafts');
    });

    this.route('deliveries', function () {
      this.route('delivery', { path: '/:delivery_id' });
    });

    this.route('drafts', function () {
      this.route('draft', { path: '/:delivery_id', resetNamespace: true }, function () {
        this.route('edit');
        this.route('preview');
      });
    });
  });

  // Activity Logs
  dslCallback.route(
    'logs',
    {
      path: 'activity_logs',
      resetNamespace: true,
    },
    function () {
      this.route('list', { path: ':tab' });
    },
  );

  // Collections
  dslCallback.route('collections', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('collection', { path: '/:collection_id' }, function () {
      this.route('index', { path: '/' });
      this.route('data');
      this.route('importing');
      this.route('usage');
    });
  });

  // Tasks (backfills)
  dslCallback.route('tasks', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
  });

  dslCallback.route('integrations', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('customerio');
    this.route('mparticle_out');
    this.route('segment_in');
    this.route('segment_out');
    this.route('cdp_in');
    this.route('cdp_out');

    this.route('warehouse', function () {
      this.route('redshift');
      this.route('bigquery');
      this.route('snowflake');

      this.route('type', { path: '/:type' }, function () {
        this.route('index', { path: '/' });
        this.route('sync');
      });
    });

    this.route('data_out', function () {
      this.route('type', { path: '/:type' });
    });

    this.route('type', { path: '/:type' }, function () {
      this.route('index', { path: '/' });
      this.route('warehouse_sync');
    });

    this.route('reporting-webhooks', function () {
      this.route('index', { path: '/' });
      this.route('new');
      this.route('edit', { path: '/:webhook_id' });
    });

    this.route('audiences', function () {
      this.route('type', { path: '/:type' }, function () {
        this.route('setup');
        this.route('settings');
      });
    });

    // OLD: before "custom forms", to be removed later
    this.route('facebook_lead_ads', function () {
      this.route('general', { path: '' }, function () {});

      this.route('form', { path: '/:id' }, function () {});
    });

    // NEW: "forms" integrations route structure
    this.route('forms', function () {
      this.route('general', { path: '' }, function () {
        this.route('settings', function () {});
      });

      this.route('form_details', { path: '/:type/:id' }, function () {});
    });

    this.route('sql-import', { path: 'sql-sync/:sql_import_id', resetNamespace: true });

    this.route('sql-imports', { path: 'sql-sync/:type', resetNamespace: true }, function () {
      this.route('index', { path: '/' });
      this.route('new');
      this.route('import', { path: ':sql_import_id' }, function () {
        this.route('index', { path: '/' });
        this.route('query');
        this.route('relationships-query');
        this.route('settings');
      });
    });
  });

  dslCallback.route('in-app-messages', { resetNamespace: true }, function () {
    this.route('message', { path: '/:message_id' });
  });

  dslCallback.route('message-library', { resetNamespace: true });

  dslCallback.route('snippets', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('new');
    this.route('snippet', { path: '/:snippet_id' });
  });

  dslCallback.route('snippets-redirect', {
    path: '/settings/actions/snippets',
    resetNamespace: true,
  });

  dslCallback.route('imports', { resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('new');
    this.route('import', { path: ':import_id' }, function () {
      this.route('index', { path: '/' });
      this.route('setup', function () {
        this.route('index', { path: '/' });
        this.route('preprocessing');
        this.route('fields');
        this.route('validating');
        this.route('preview');
        this.route('review');
        this.route('importing');
      });
    });
  });

  dslCallback.route('exports', { resetNamespace: true }, function () {
    this.route('export', {
      path: ':export_id',
      resetNamespace: true,
    });
  });

  // these routes are only for redirecting legacy URLs
  dslCallback.route('legacy-redirects', {
    path: 'v2/*v2path',
    resetNamespace: true,
  });
  dslCallback.route('legacy-redirects', {
    path: 'triggered',
    resetNamespace: true,
  });
  dslCallback.route('legacy-redirects', {
    path: 'triggered/new',
    resetNamespace: true,
  });

  dslCallback.route('data-index', { resetNamespace: true }, function () {
    this.route('type', { path: ':type' }, function () {
      this.route('list', { path: '/' }, function () {
        this.route('index', { path: '/' });
      });
      this.route('item', { path: ':item_id' });
    });
  });
  dslCallback.route('analysis', { resetNamespace: true });
  dslCallback.route('assets', { resetNamespace: true });
}

function workspaceSettingsRoute(dslCallback) {
  dslCallback.route('workspace-settings', { path: 'settings', resetNamespace: true }, function () {
    this.route('index', { path: '/' });
    this.route('edit');
    this.route('confirm_delete');
    this.route('anonymous_events');
    this.route('api_credentials');
    this.route('language');
    this.route('merge_options');
    this.route('subscription_center', function () {
      this.route('index', { path: '/' });
      this.route('topics', function () {
        this.route('index', { path: '/' });
        this.route('set_preferences', function () {
          this.route('type', { path: '/' });
        });
      });
      this.route('edit');
      this.route('branding');
      this.route('branding_edit', { path: 'branding/edit' });
      this.route('settings');
      this.route('localization', function () {
        this.route('edit');
      });
    });
    this.route('objects', function () {
      this.route('index', { path: '/' });
      this.route('new');
      this.route('edit', { path: 'edit/:object_type' });
      this.route('delete', { path: 'delete/:object_type' });
    });

    // Settings pages for actions and integrations
    this.route('actions', { resetNamespace: true }, function () {
      this.route('email', function () {
        this.route('index', { path: '/' });
        this.route('sending_domains');
        this.route('from_addresses');
        this.route('deliverability');
        this.route('smtp');
        this.route('suppression_list');
      });
      this.route('push', function () {
        this.route('index', { path: '/' });
        this.route('android');
        this.route('ios');
      });
      this.route('in_app');
      this.route('slack');
      this.route('twilio');
      this.route('urban_airship');
      this.route('time_zone_match');
      this.route('url_params', function () {
        this.route('index', { path: '/' });
        this.route('edit');
      });
      this.route('messaging_limit', function () {});
    });
  });
}

AppRouter.map(function () {
  let useNewWorkspaceRoutes = (config.isHydraMode ?? false) && (config.hydraProps?.useNewWorkspacesUrls ?? false);

  this.route('errors', { path: '/errors/:error_id' });
  // eslint-disable-next-line ember/no-capital-letters-in-routes
  this.route('pageNotFoundError', { path: '*:' });
  this.route('loading');
  this.route('post_cancel');

  this.route('authenticated', { path: '', resetNamespace: true }, function () {
    this.route('no_accessible_workspaces', { resetNamespace: true });

    this.route('app', { path: '', resetNamespace: true }, function () {
      this.route('environments', { path: useNewWorkspaceRoutes ? '' : '/env', resetNamespace: true }, function () {
        if (useNewWorkspaceRoutes) {
          this.route('environments_create', {
            path: '/settings/workspaces/create',
            resetNamespace: true,
          });

          this.route('environment', { path: '/workspaces/:environment_id', resetNamespace: true }, function () {
            this.route('environment_content', { path: '/journeys', resetNamespace: true }, function () {
              workspaceRoutes(this);
            });
            workspaceSettingsRoute(this);
          });
        } else {
          this.route('environments_create', {
            path: 'create',
            resetNamespace: true,
          });

          this.route('environment', { path: ':environment_id', resetNamespace: true }, function () {
            workspaceRoutes(this);
            workspaceSettingsRoute(this);
          });
        }
      });
      this.route('settings', { resetNamespace: true }, function () {
        this.route('account', function () {
          this.route('edit');
        });
        this.route('api_credentials');
        this.route('receipts', function () {});
        this.route('privacy');
        this.route('billing', function () {
          this.route('your-plan');
          this.route('available-plans', function () {
            this.route('journeys');
            this.route('data-pipelines');
          });
          this.route('payment-methods');
          this.route('billing-history');
          this.route('checkout');
        });
        this.route('security', function () {
          this.route('sso');
        });
        this.route('subscription');
        this.route('team', function () {
          this.route('set_password');
        });
        this.route('workspaces', function () {
          this.route('new');
        });
        this.route('personal', function () {
          this.route('index', { path: '/' });
          this.route('security', function () {
            this.route('new');
            this.route('backup_codes');
            this.route('new_backup_codes');
            this.route('new_totp');
          });
        });
        this.route('experimental-features');
      });
      this.route('product-activation', { path: 'activate/:product', resetNamespace: true }, function () {
        this.route('experience');
        this.route('goals');
      });
      this.route('internal-tools', { resetNamespace: true }, function () {
        this.route('inspect');
        this.route('cio-cli-tool');
      });
      this.route('trial-expired', { path: 'trial-expired/journeys', resetNamespace: true });
    });

    this.route('confirm_account_notice');
    this.route('account_canceled');

    this.route('post_registration', function () {
      this.route('role');
      this.route('goal');
      this.route('channels');
      this.route('tooling');
      this.route('connect');
      this.route('ready');
    });

    this.route('har-sanitizer', { resetNamespace: true });

    // legacy British English "cancelled" (double "l") URL, with redirect:
    this.route('account_cancelled'); // eslint-disable-line spellcheck/spell-checker

    // legacy paths in Unity - placeholder routes with redirects:
    this.route('account_cancelled-redirect', { path: '/account/cancelled' }); // eslint-disable-line spellcheck/spell-checker
    this.route('post_registration-redirect', {
      path: '/post_registration/new',
    }); // legacy path in Unity
    this.route('2fa_setup', function () {
      this.route('new_totp');
    });
  });

  this.route('login', function () {
    this.route('two_factor_authentication');
    this.route('sso');
    this.route('password');
    this.route('2fa_email_link');
    this.route('choose_user');
  });

  this.route('logout');

  this.route('reset_password', function () {
    this.route('new_password');
  });

  this.route('signup', function () {
    this.route('password');
    this.route('company');
    this.route('check-email');
  });

  this.route('confirm_account');

  this.route('oauth', function () {
    this.route('google');
  });

  this.route('integrations-public', { path: '/integrations' }, function () {
    this.route('type', { path: '/:type' });
  });

  this.route('vwb-demo', { resetNamespace: true });

  // legacy paths in Unity - placeholder routes with redirects:
  this.route('confirm-account-redirect', { path: '/account/confirm' });
  this.route('signup-redirect', { path: '/users/register' });
  this.route('signup-redirect', { path: '/users/signup' });
  this.route('reset_password-redirect', { path: '/users/password/new' });
  this.route('reset_password-new_password-redirect', {
    path: '/users/password/edit',
  });
  this.route('login-redirect', { path: '/users/login' });
  this.route('account-redirect', { path: '/account' });
  this.route('account-edit-redirect', { path: '/account/edit' });
  this.route('account-subscription-redirect', {
    path: '/account/subscription',
  });
  this.route('account-subscription-new-redirect', {
    path: '/account/subscription/new',
  });
  this.route('account-security-redirect', { path: '/account/security' });
  this.route('account-security-new-redirect', {
    path: '/account/security/new',
  });
  this.route('account-security-backup_codes-redirect', {
    path: '/account/security/backup_codes',
  });
  this.route('account-api_credentials-redirect', {
    path: '/account/api_credentials',
  });
  this.route('account-api_credentials-new-redirect', {
    path: '/account/api_credentials/new',
  });
  this.route('account-api_credentials-confirm_delete-redirect', {
    path: '/account/api_credentials/:id/confirm_delete',
  });
  this.route('account-receipts-redirect', { path: '/account/receipts' });
  this.route('account-receipts-show-redirect', {
    path: '/account/receipts/:id',
  });
  this.route('account-team-redirect', { path: '/account/team' });
  this.route('account-team-passwords-edit-redirect', {
    path: '/account/team/:id/passwords/edit',
  });
  this.route('account-workspaces-redirect', { path: '/account/workspaces' });
  this.route('account-workspaces-new-redirect', {
    path: '/account/workspaces/new',
  });
  this.route('account-workspaces-edit-redirect', {
    path: '/account/workspaces/:id/edit',
  });
  this.route('account-workspaces-confirm_delete-redirect', {
    path: '/account/workspaces/:id/confirm_delete',
  });
});
